import React from "react";
import { CommentNode } from "./types";
import {
  formatCommentTime,
  getCommentDisplayExcerpt,
  getCommentDisplayText,
  getCommentScopeClass,
  getCommentScopeLabel,
} from "./commentText";
import { getCommentPath, getRootForComment } from "./commentTreeModel";

interface CommentReaderViewProps {
  roots: CommentNode[];
  byId: Map<number, CommentNode>;
  selectedComment: CommentNode | null;
  currentEditId?: number | null;
  onSelect: (commentId: number) => void;
}

const CommentReaderView: React.FC<CommentReaderViewProps> = ({
  roots,
  byId,
  selectedComment,
  currentEditId,
  onSelect,
}) => {
  const selectedRoot = getRootForComment(byId, selectedComment);
  const path = selectedComment ? getCommentPath(byId, selectedComment.id) : [];
  const parent = selectedComment?.parentId ? byId.get(selectedComment.parentId) ?? null : null;
  const siblings = parent ? parent.replies : selectedRoot ? roots : [];

  if (!selectedComment) {
    return (
      <div className="comment-explorer-reader-empty">
        Select a thread to read its branch.
      </div>
    );
  }

  const scopeClass = getCommentScopeClass(selectedComment, currentEditId);

  return (
    <div className="comment-explorer-reader-view">
      <div className="comment-explorer-reader-main">
        <div className="comment-explorer-breadcrumbs" aria-label="Selected comment path">
          {path.map((comment, index) => (
            <button
              key={comment.id}
              type="button"
              className={comment.id === selectedComment.id ? "is-current" : ""}
              onClick={() => onSelect(comment.id)}
            >
              {index === 0 ? "Root" : `Reply ${index}`} · {getCommentDisplayExcerpt(comment, 30)}
            </button>
          ))}
        </div>

        {parent && (
          <button type="button" className="comment-explorer-parent-link" onClick={() => onSelect(parent.id)}>
            ↑ Parent: {getCommentDisplayExcerpt(parent, 90)}
          </button>
        )}

        <article className={`comment-explorer-reader-card is-primary ${scopeClass} ${selectedComment.isRemoved ? "is-removed" : ""}`}>
          <div className="comment-explorer-comment-head">
            <div>
              <strong>{selectedComment.user?.name || "Unknown user"}</strong>
              <span>{formatCommentTime(selectedComment.createdAt)}</span>
            </div>
            <span className={`comment-explorer-scope-badge ${scopeClass}`}>
              {getCommentScopeLabel(selectedComment, currentEditId)}
            </span>
          </div>
          <p>{getCommentDisplayText(selectedComment)}</p>
        </article>

        <div className="comment-explorer-reader-section-title">
          Direct replies ({selectedComment.replies.length})
        </div>

        {selectedComment.replies.length === 0 ? (
          <div className="comment-explorer-reader-empty is-small">
            No direct replies yet. Use the Inspector to reply to this comment.
          </div>
        ) : (
          <div className="comment-explorer-direct-replies">
            {selectedComment.replies.map((reply) => (
              <button
                key={reply.id}
                type="button"
                className={`comment-explorer-reply-preview ${reply.id === selectedComment.id ? "is-selected" : ""}`}
                onClick={() => onSelect(reply.id)}
              >
                <span>{reply.user?.name || "Unknown user"} · {formatCommentTime(reply.createdAt)}</span>
                <strong>{getCommentDisplayExcerpt(reply, 120)}</strong>
                <em>{reply.totalReplyCount} nested replies</em>
              </button>
            ))}
          </div>
        )}
      </div>

      <aside className="comment-explorer-reader-rail">
        <h4>Sibling rail</h4>
        {siblings.map((sibling) => (
          <button
            key={sibling.id}
            type="button"
            className={sibling.id === selectedComment.id ? "is-active" : ""}
            onClick={() => onSelect(sibling.id)}
          >
            <span>{sibling.user?.name || "Unknown user"}</span>
            {getCommentDisplayExcerpt(sibling, 72)}
          </button>
        ))}

        {selectedRoot && (
          <>
            <h4>Thread root</h4>
            <button type="button" onClick={() => onSelect(selectedRoot.id)}>
              <span>{selectedRoot.user?.name || "Unknown user"}</span>
              {getCommentDisplayExcerpt(selectedRoot, 72)}
            </button>
          </>
        )}
      </aside>
    </div>
  );
};

export default CommentReaderView;
